import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  ViewChild,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

import { AuxTabComponent } from '@shared/components/tab-group/aux-tab.component';
import { TabGroupComponent } from '@shared/components/tab-group/tab-group.component';
import { DataSource } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { InvoiceModel } from '@shared/store/invoice/invoice.model';
import { Utils } from '@shared/utils/utils';

import { InvoiceItemsIntegrationComponent } from './invoice-items-integration/invoice-items-integration.component';
import { InvoiceItemsViaPdfComponent } from './invoice-items-via-pdf/invoice-items-via-pdf.component';
import { MapActivitiesTotals } from './map-activities/map-activities-modal/map-activities-modal.model';
import { MapActivitiesTabComponent } from './map-activities/map-activities-tab.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TabGroupComponent,
    AuxTabComponent,
    InvoiceItemsIntegrationComponent,
    InvoiceItemsViaPdfComponent,
    MapActivitiesTabComponent,
  ],
  selector: 'aux-invoice-tabs',
  templateUrl: 'invoice-tabs.component.html',
})
export class InvoiceTabsComponent implements OnInit {
  launchDarklyService = inject(LaunchDarklyService);
  route = inject(ActivatedRoute);
  router = inject(Router);

  invoice = input.required<InvoiceModel>();

  integrationTabLabel = computed(() => {
    const invoice = this.invoice();

    if (invoice.data_source_id) {
      return `Line Items (via ${this.getIntegrationSourceText(invoice.data_source_id)})`;
    }

    return '';
  });

  invoiceItemsViaPdf = viewChild(InvoiceItemsViaPdfComponent);

  @ViewChild('activitiesTab') activitiesTab!: MapActivitiesTabComponent;
  invoiceAllocatedTotals$ = new BehaviorSubject<MapActivitiesTotals>({
    ACTIVITY_DISCOUNT: 0,
    ACTIVITY_INVESTIGATOR: 0,
    ACTIVITY_PASSTHROUGH: 0,
    ACTIVITY_SERVICE: 0,
    invoice_total: 0,
  });
  isIntegrationItemsEnabled = this.launchDarklyService.$select(
    (f) => f.tab_invoice_line_items_integration
  );
  isMapActivitiesEnabled = this.launchDarklyService.$select((f) => f.map_invoice_to_activities_tab);
  isOcrItemsEnabled = this.launchDarklyService.$select((f) => f.tab_invoice_line_items_ocr);
  @ViewChild('tabGroupComponent') tabGroupComponent!: TabGroupComponent;
  readonly utils = Utils;

  ngOnInit() {
    const { isIntegrationTabActive } = this.route.snapshot.queryParams;

    if (isIntegrationTabActive) {
      this.tabGroupComponent?.setActiveTabIndex(2);
      this.router
        .navigate([], {
          queryParams: { isIntegrationTabActive: null },
          queryParamsHandling: 'merge',
        })
        .catch(console.error);
    }
  }

  canDeactivate() {
    const cmp = this.invoiceItemsViaPdf();

    return cmp ? cmp.canDeactivate() : true;
  }

  async changeToMapActivitiesTab(lineItemsType: 'integration' | 'pdf'): Promise<void> {
    this.tabGroupComponent?.setActiveTabIndex(0);
    await this.activitiesTab.openMapActivitiesModal(lineItemsType);
  }

  private getIntegrationSourceText(dataSource: DataSource): string {
    if (dataSource === DataSource.DATA_SOURCE_AUXILIUS) {
      return 'bulk upload';
    }

    return `${Utils.readableDataSource(dataSource)} Integration`;
  }
}
